/**
 * BYOC name validation.
 *
 * Runs in the UI, as the user types — not on submit, and not in the backend
 * after an upload has already been accepted. The name becomes a URL path
 * segment, a storage key segment and a deployment identifier, so every rule is
 * enforced where the user can still act on it.
 *
 * A rejected name is never silently corrected: turning "Sales Report!" into
 * "sales-report" behind the user's back produces a link they did not expect
 * and cannot predict. The backend re-validates on receipt as a safety net
 * against a malformed request, not as the user's experience of the rule.
 */

import { NAME_MAX_LENGTH, NAME_PATTERN } from "./constants";

export const VALIDATION = {
  EMPTY: "empty",
  VALID: "valid",
  ERROR: "error",
  WARNING: "warning",
};

/** Levenshtein distance, capped — we only care about "close", not how far. */
export const editDistance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, substitution);
    }
    previous = current;
  }

  return previous[b.length];
};

/**
 * Names are case-sensitive in both storage and the URL, so `SMDMockup` and
 * `smdmockup` are genuinely different apps. A case-only difference is
 * therefore allowed but worth surfacing, because it is rarely intended.
 */
const NEAR_MISS_MAX_DISTANCE = 2;

export const findNearMiss = (name, existingNames = []) => {
  if (!name) return null;
  const lowered = name.toLowerCase();

  const caseOnly = existingNames.find(
    (existing) => existing !== name && existing.toLowerCase() === lowered
  );
  if (caseOnly) return caseOnly;

  return (
    existingNames.find(
      (existing) =>
        existing !== name && editDistance(lowered, existing.toLowerCase()) <= NEAR_MISS_MAX_DISTANCE
    ) || null
  );
};

/** Name the offending character rather than describing the rule abstractly. */
export const firstDisallowedCharacter = (name) => {
  const match = name.match(/[^A-Za-z0-9_-]/);
  if (!match) return null;
  return match[0] === " " ? " " : match[0];
};

/**
 * Validate a candidate name against the rules and the existing solutions.
 *
 * @param {string} rawName        what the user has typed so far
 * @param {string[]} existingNames names already onboarded
 * @returns {{status: string, message: string, nearMiss?: string, takenBy?: string}}
 */
export const validateName = (rawName, existingNames = []) => {
  const name = rawName == null ? "" : String(rawName);

  if (!name.trim()) {
    return { status: VALIDATION.EMPTY, message: "" };
  }

  const disallowed = firstDisallowedCharacter(name);
  if (disallowed) {
    const described = disallowed === " " ? "Spaces aren't" : `"${disallowed}" isn't`;
    return {
      status: VALIDATION.ERROR,
      message: `${described} allowed. Use letters, numbers, hyphen or underscore.`,
    };
  }

  if (!/^[A-Za-z0-9]/.test(name)) {
    return {
      status: VALIDATION.ERROR,
      message: "Start with a letter or a number.",
    };
  }

  if (name.length > NAME_MAX_LENGTH) {
    return {
      status: VALIDATION.ERROR,
      message: `Too long — ${name.length} characters, the limit is ${NAME_MAX_LENGTH}.`,
    };
  }

  // Defence in depth: the checks above should cover everything the pattern
  // rejects, so reaching this means the pattern and the checks have drifted.
  if (!NAME_PATTERN.test(name)) {
    return {
      status: VALIDATION.ERROR,
      message: "That name can't be used. Use letters, numbers, hyphen or underscore.",
    };
  }

  const taken = existingNames.find((existing) => existing === name);
  if (taken) {
    return {
      status: VALIDATION.ERROR,
      message: "Already taken. Did you mean to update it?",
      takenBy: taken,
    };
  }

  const nearMiss = findNearMiss(name, existingNames);
  if (nearMiss) {
    return {
      status: VALIDATION.WARNING,
      message: `Close to an existing solution: ${nearMiss}. Continue only if these are meant to be separate.`,
      nearMiss,
    };
  }

  return { status: VALIDATION.VALID, message: "available" };
};

/** Only an outright error blocks submission; a near-miss warning does not. */
export const blocksSubmission = (result) =>
  !result || result.status === VALIDATION.ERROR || result.status === VALIDATION.EMPTY;

/* ---- File validation ---- */

export const validateFile = (file, { acceptedTypes = [], maxBytes = Infinity } = {}) => {
  if (!file) {
    return { status: VALIDATION.EMPTY, message: "" };
  }

  const name = file.name || "";
  const extension = name.includes(".") ? name.slice(name.lastIndexOf(".")).toLowerCase() : "";

  if (acceptedTypes.length && !acceptedTypes.includes(extension)) {
    return {
      status: VALIDATION.ERROR,
      message: `${extension || "That file type"} isn't supported. Upload ${acceptedTypes.join(" or ")}.`,
    };
  }

  if (file.size > maxBytes) {
    const limitMb = Math.round(maxBytes / (1024 * 1024));
    return {
      status: VALIDATION.ERROR,
      message: `Too large — the limit is ${limitMb} MB.`,
    };
  }

  return { status: VALIDATION.VALID, message: "" };
};
